import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react'
import { apiClient } from '../services/api'
import { activityMonitor } from '../services/activityMonitor'

// --- Type Definitions ---

interface AuthUser {
  username: string
  email?: string
  roles?: string[]
  knowledge_claims?: string[]
  app_access?: string[]  // Phase 76: Application access gating
  sessionType?: string
  restoredFromCookie?: boolean
  lastValidated?: string
  password_change_required?: boolean
  first_login?: boolean
  last_password_change?: string
  [key: string]: unknown  // Allow additional backend fields
}

interface LoginCredentials {
  username: string
  password: string
}

interface LoginResponse {
  user: AuthUser
  sessionManagement: string
  csrfToken: string | null
}

interface AuthContextValue {
  // User state
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  sessionType: string

  // Authentication methods
  login: (credentials: LoginCredentials) => Promise<AuthUser>
  logout: () => Promise<void>
  validateSession: () => Promise<boolean>
  restoreSessionFromCookie: () => Promise<AuthUser | null>

  // Multi-role helpers
  hasRole: (role: string) => boolean
  hasAnyRole: (...roles: string[]) => boolean

  // Phase 76: App access helpers
  hasAccess: (appKey: string) => boolean

  // Session state helpers
  isCookieBased: boolean
  isTokenBased: boolean

  // Loading state helpers
  isInitializing: boolean
  isAuthenticating: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Keep the old export for backward compatibility
export const useAuthContext = useAuth

// AuthProvider props: children

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  useEffect(() => {
    const initializeAuth = async () => {
      // Phase 34 Task 3.2: Enhanced cookie-based session initialization with persistence
      setIsLoading(true)

      console.log('Initializing authentication - checking for persistent cookie-based session')

      try {
        // Pure cookie-based session validation with retry logic for persistence
        const sessionData = await apiClient.validateSession()

        if (sessionData.valid && sessionData.user) {
          console.log('Session successfully restored from cookie on page load:', sessionData.user.username)

          // Phase 66 (BUG-004): Use spread to preserve ALL backend fields
          // Phase 76 M5: is_admin removed; use app_access for access checks
          const restoredUser: AuthUser = {
            ...sessionData.user,  // Preserve all backend fields including roles, knowledge_claims, app_access
            sessionType: 'cookie-only',
            restoredFromCookie: true,
            lastValidated: new Date().toISOString()
          }

          setUser(restoredUser)
          console.log('Authentication state successfully persisted across page refresh')

          // Phase 63: Start activity monitoring for session heartbeat
          activityMonitor.start()

          setIsLoading(false)
          return
        } else {
          console.log('Session validation returned invalid session data - no persistent session found')
        }
      } catch (error) {
        // Enhanced error handling for session validation during initialization
        if (error.message?.includes('Session expired')) {
          console.log('Persistent session expired during initialization')
        } else if (error.message?.includes('network error')) {
          console.warn('Network error during session validation - authentication state unknown')
          // For network errors during initialization, we'll show loading state longer
          // and let the user try to interact, which will trigger another validation
        } else {
          console.log('Cookie-based session validation failed during initialization:', error.message)
        }
      }

      // No valid authentication found or session validation failed
      console.log('No persistent authentication found - user needs to login')
      setUser(null)
      setIsLoading(false)
    }

    // Initialize authentication on component mount (page load/refresh)
    initializeAuth()
  }, [])

  // Enhanced persistence: Re-validate session when user returns to tab (Phase 34 Task 3.2)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      // Only re-validate if user is authenticated and tab becomes visible
      if (!document.hidden && user && user.sessionType === 'cookie-only') {
        console.log('Tab became visible - re-validating session for persistence')

        try {
          await validateSession()
          console.log('Session re-validation successful on tab focus')
        } catch (error) {
          console.log('Session re-validation failed on tab focus:', error.message)
          // Error handling is already done in validateSession method
        }
      }
    }

    // Add event listener for tab visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Cleanup event listener
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [user]) // Re-run when user state changes

  // Phase 34 Task 3.2: Enhanced cookie-based login with error handling
  const login = async (credentials: LoginCredentials): Promise<AuthUser> => {
    try {
      console.log('Attempting login with pure cookie-based authentication')

      const response = await apiClient.login(credentials)

      // Validate response structure
      if (!response || !response.user) {
        console.error('Invalid login response structure:', response)
        throw new Error('Invalid response from server')
      }

      // Set user with pure cookie-based session
      const userData: AuthUser = {
        ...response.user,
        sessionType: 'cookie-only'
      }

      setUser(userData)
      console.log('Login successful - user authenticated with cookie-only session')

      // Phase 63: Start activity monitoring for session heartbeat
      activityMonitor.start()

      return userData
    } catch (error) {
      console.error('Login failed in AuthContext:', error.message)

      // Clear any partial user state
      setUser(null)

      // Re-throw with enhanced error information
      if (error.message?.includes('Invalid credentials')) {
        throw new Error('Invalid username or password')
      } else if (error.message?.includes('network error') || error.message?.includes('connect')) {
        throw new Error('Unable to connect to server. Please check your connection and try again.')
      } else if (error.message?.includes('Server error')) {
        throw new Error('Server is temporarily unavailable. Please try again later.')
      } else {
        throw error
      }
    }
  }

  // Phase 34 Task 3.1: Pure cookie-based logout
  // Phase 76 M5: Removed adminAuthService.clearSessionCache() (deprecated)
  const logout = async (): Promise<void> => {
    try {
      console.log('Logging out - clearing cookies')
      setIsLoading(true)

      // Phase 63: Stop activity monitoring before logout
      activityMonitor.stop()

      // Perform logout via API client (clears cookies only)
      await apiClient.logout()

      // Clear user state
      setUser(null)

      console.log('Logout completed - redirecting to login')

      // Clear any cached data and redirect
      window.location.href = '/login'
    } catch (error) {
      console.error('Logout error:', error)

      // Force logout even if API call fails
      setUser(null)
      window.location.href = '/login'
    } finally {
      setIsLoading(false)
    }
  }

  // Phase 34 Task 3.2: Enhanced cookie-based session validation with error handling
  const validateSession = async (): Promise<boolean> => {
    try {
      console.log('Validating cookie-based session')

      const sessionData = await apiClient.validateSession()
      if (sessionData.valid && sessionData.user) {
        // Update user data from session
        setUser(prevUser => ({
          ...prevUser,
          ...sessionData.user,
          sessionType: 'cookie-only'
        }))
        console.log('Session validation successful for user:', sessionData.user.username)
        return true
      } else {
        console.log('Session validation returned invalid session')
        throw new Error('Session is not valid')
      }
    } catch (error) {
      console.error('Session validation failed in AuthContext:', error.message)

      // Handle different types of validation errors
      if (error.message?.includes('network error')) {
        console.warn('Network error during session validation - not logging out automatically')
        throw new Error('Unable to validate session due to network error')
      } else if (error.message?.includes('Session expired')) {
        console.log('Session expired - logging out')
        await logout()
        throw new Error('Your session has expired. Please log in again.')
      } else {
        console.log('Session invalid - logging out')
        await logout()
        throw error
      }
    }
  }

  // Enhanced session restoration from cookies (Phase 34 Task 3.2)
  // Phase 66 (BUG-005): Returns the restored user object (or null) instead of boolean
  // This allows callers to check user data immediately without waiting for state update
  const restoreSessionFromCookie = async (): Promise<AuthUser | null> => {
    try {
      console.log('Attempting to restore session from cookie')
      const sessionData = await apiClient.validateSession()

      if (sessionData.valid && sessionData.user) {
        console.log('Session restoration successful for user:', sessionData.user.username)

        const restoredUser: AuthUser = {
          ...sessionData.user,
          email: sessionData.user.email || sessionData.user.username + '@example.com',
          sessionType: 'cookie-only',
          restoredFromCookie: true,
          lastValidated: new Date().toISOString()
        }

        setUser(restoredUser)
        return restoredUser  // Phase 66 (BUG-005): Return user object for immediate checking
      } else {
        console.log('Session restoration failed - invalid session data')
        return null
      }
    } catch (error) {
      console.error('Session restoration failed:', error.message)

      // Don't automatically logout on restoration failure
      // Let the user try to interact and handle errors then
      return null
    }
  }

  // Phase 61 (T7.7): Helper to check if user has a specific role
  // Phase 66 (BUG-006): Removed deprecated single role fallback, uses only roles array
  const hasRole = (role: string): boolean => {
    if (!user) return false
    return user.roles?.includes(role) ?? false
  }

  // Phase 61 (T7.7): Helper to check if user has any of the specified roles
  // Phase 66 (BUG-006): Removed deprecated single role fallback, uses only roles array
  const hasAnyRole = (...roles: string[]): boolean => {
    if (!user) return false
    return roles.some(role => user.roles?.includes(role))
  }

  // Phase 76: Check if user has access to a specific app (O(1) Set lookup)
  const appAccessSet = useMemo(
    () => new Set(user?.app_access || []),
    // Use serialized key for content-based comparison instead of referential equality
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.app_access?.join(',') ?? '']
  )

  const hasAccess = useCallback(
    (appKey: string): boolean => appAccessSet.has(appKey),
    [appAccessSet]
  )

  // Phase 34 Task 3.1: Pure cookie-based context value
  // Phase 61 (T7.7): Added multi-role support helpers
  // Phase 76 M5: Removed isAdmin, validateAdminSession, isUserAdmin — use hasAccess() instead
  const value: AuthContextValue = {
    user,
    isAuthenticated: !!user,
    isLoading,
    sessionType: 'cookie-only',

    // Authentication methods
    login,
    logout,
    validateSession,
    restoreSessionFromCookie,

    // Phase 61 (T7.7): Multi-role helpers
    hasRole,
    hasAnyRole,

    // Phase 76: App access helpers
    hasAccess,

    // Session state helpers
    isCookieBased: true,  // Always true for pure cookie-based auth
    isTokenBased: false,  // Always false for pure cookie-based auth

    // Loading state helpers
    isInitializing: isLoading && !user,
    isAuthenticating: isLoading && !!user,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

AuthProvider.displayName = 'AuthProvider'